import React from 'react';

import {
  useAlloyFeatureFlag,
  useUsers,
  useCompanies,
} from '@adsk/alloy-provider';
import Badge from '@adsk/alloy-react-badge';
import FormField from '@adsk/alloy-react-form-field';
import { useLocale } from '@adsk/alloy-react-locale';
import Panel from '@adsk/alloy-react-panel';

import { Filter, AssigneePickerFilter, VirtualFilter } from './common';
import ReferencePickerPlugin from '../../../types/ReferencePickerPlugin';
import {
  statuses,
  priorities,
  ASSIGNEE_TYPES,
  SUBMITTALS_ROLES_COMPANY_FF,
} from '../consts';
import { useSpecs } from '../hooks';
import {
  StatusOption,
  SubmittalsItem,
  FilterOption,
  ItemWithGroup,
} from '../types';

const FiltersPanel: ReferencePickerPlugin<SubmittalsItem>['FiltersPanel'] = ({
  filters,
  setFilters,
  onClose,
  open,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const { data: users } = useUsers();
  const { data: companies } = useCompanies();
  const { specs, isLoading } = useSpecs();
  const { value: isRolesAndCompaniesEnabled } = useAlloyFeatureFlag(
    SUBMITTALS_ROLES_COMPANY_FF,
    true,
  );
  function updateFilters(key: string, value: unknown[]) {
    const newFilters = { ...filters };

    if (!value || (Array.isArray(value) && value.length === 0)) {
      // Need to delete the entire key from the object because otherwise
      // ReferencePicker will understand it as if the filter exists
      // (even if the value is undefined or null)
      delete newFilters[key];
    } else if (Array.isArray(value)) {
      newFilters[key] = (value as ItemWithGroup[]).map(
        (option: ItemWithGroup) => option.value,
      );
    } else {
      newFilters[key] = value;
    }

    setFilters(newFilters);
  }
  function updateBallInCourtFilters(value?: ItemWithGroup[]) {
    // setControlledSelectedItems(value as DefaultItem[]);
    const newFilters = { ...filters };

    if (!value || (Array.isArray(value) && value.length === 0)) {
      // Need to delete the entire key from the object because otherwise
      // ReferencePicker will understand it as if the filter exists
      // (even if the value is undefined or null)
      delete newFilters['ballInCourtUsers'];
      delete newFilters['ballInCourtCompanies'];
      delete newFilters['ballInCourtRoles'];
    } else if (Array.isArray(value)) {
      // setControlledSelectedItems(value as DefaultItem[]);
      const ballInCourtUsersValues = value.filter(
        (option) => option.group === ASSIGNEE_TYPES.USER,
      );
      if (ballInCourtUsersValues.length > 0) {
        newFilters['ballInCourtUsers'] = ballInCourtUsersValues.map(
          (option) => option.value,
        );
      }

      const ballInCourtCompanyValues = value.filter(
        (option) => option.group === ASSIGNEE_TYPES.COMPANY,
      );
      if (ballInCourtCompanyValues.length > 0) {
        newFilters['ballInCourtCompanies'] = ballInCourtCompanyValues.map(
          (option) => option.value,
        );
      }
      const ballInCourtRoleValues = value.filter(
        (option) => option.group === ASSIGNEE_TYPES.ROLE,
      );
      if (ballInCourtRoleValues.length > 0) {
        newFilters['ballInCourtRoles'] = ballInCourtRoleValues.map(
          (option) => option.value,
        );
      }
    } else {
      if (value['group'] === ASSIGNEE_TYPES.COMPANY) {
        newFilters['ballInCourtCompanies'] = value;
      } else if (value['group'] === ASSIGNEE_TYPES.ROLE) {
        newFilters['ballInCourtRoles'] = value;
      } else {
        newFilters['ballInCourtUsers'] = value;
      }
    }

    setFilters(newFilters);
  }

  const title = T.translate('FILTERS')?.toString();
  const userOptions = users.map((user) => ({
    label: user.name || user.email,
    subtext: companies.find((company) => company.id === user.companyId)?.name,
    value: user.autodeskId,
  }));

  const specOptions = specs.map((spec) => ({
    label: `${spec.identifier} - ${spec.title}`,
    value: spec.id,
  }));
  const statusOptions: StatusOption[] = Object.entries(statuses)
    .filter(([key, _status]) => key !== 'void')
    .map(([key, status]) => ({
      value: key,
      label: T.translate(status.stateText || status.statusText),
      statusText: T.translate(status.statusText),
      stateText: status.stateText && T.translate(status.stateText),
      color: status.color,
    }));
  const priorityOptions: FilterOption[] = Object.entries(priorities).map(
    ([key, priority]) => ({
      value: key,
      label: T.translate(priority.label),
    }),
  );

  return (
    <Panel title={title} open={open} onClose={onClose}>
      <Panel.Body>
        <FormField
          style={{ marginBottom: 20 }}
          label={T.translate('FILTERS_SPEC')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <VirtualFilter
            options={specOptions}
            placeholder={T.translate('FILTERS_PLACEHOLDER_SPEC')?.toString()}
            onChange={(value) => updateFilters('specId', value)}
            value={filters.specId as unknown[]}
            isLoading={isLoading}
          />
        </FormField>
        <Filter
          label={T.translate('FILTERS_STATUS')}
          options={statusOptions}
          placeholder={T.translate('FILTERS_PLACEHOLDER_STATUS')?.toString()}
          renderOption={(option) => (
            <Badge
              color={(option as StatusOption).color}
              variant={Badge.VARIANTS.LINE}
              subStatus={(option as StatusOption).stateText}
              stacked={false}
            >
              {(option as StatusOption).statusText}
            </Badge>
          )}
          onChange={(value) => updateFilters('stateId', value)}
          value={filters.stateId as unknown[]}
        />
        {!isRolesAndCompaniesEnabled && (
          <FormField
            style={{ marginBottom: 20 }}
            label={T.translate('FILTERS_BALL_IN_COURT')}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
          >
            <VirtualFilter
              options={userOptions}
              placeholder={T.translate(
                'FILTERS_PLACEHOLDER_BALL_IN_COURT',
              )?.toString()}
              onChange={(value) => updateFilters('ballInCourt', value)}
              value={filters.ballInCourt as unknown[]}
            />
          </FormField>
        )}

        {isRolesAndCompaniesEnabled && (
          <AssigneePickerFilter
            label={T.translate('FILTERS_BALL_IN_COURT')}
            onChange={(selectedItems) =>
              updateBallInCourtFilters(selectedItems)
            }
          />
        )}
        <Filter
          label={T.translate('FILTERS_PRIORITY')}
          options={priorityOptions}
          placeholder={T.translate('FILTERS_PLACEHOLDER_PRIORITY')?.toString()}
          onChange={(value) => updateFilters('priority', value)}
          value={filters.priority as unknown[]}
        />
      </Panel.Body>
    </Panel>
  );
};

FiltersPanel.displayName = 'SubmittalsFiltersPanel';

export default FiltersPanel;
